import React, { useRef, useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface SuccessStory {
  id: string;
  company: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
    icon: string;
  }[];
  quote: string;
  author: string;
  role: string;
  image: string;
  alt: string;
}

interface SuccessStoriesProps {
  className?: string;
}

const SuccessStories: React.FC<SuccessStoriesProps> = ({ className = '' }) => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setIsInView(true);},
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stories: SuccessStory[] = [
  {
    id: 'telecom-leader',
    company: 'European Telecom Group',
    industry: 'Telecommunications',
    challenge: 'High customer churn due to poor support experience and network issues',
    solution: 'Implemented KnowMax + DeviceMax for intelligent support and proactive device management',
    results: [
    { metric: 'Improvement in documentation', value: '21%', icon: 'FaceSmileIcon' },
    { metric: 'Improvement in FCR', value: '12%', icon: 'ArrowTrendingDownIcon' },
    { metric: 'Saved annually', value: '$400,000', icon: 'CurrencyDollarIcon' }],

    quote: 'UnifyMax transformed our customer support from reactive to proactive. The AI-powered insights helped us prevent issues before customers even noticed them.',
    author: 'Sarah Chen',
    role: 'Chief Customer Officer',
    image: '/assets/images/15_-1771919860863.jpg',
    alt: 'Professional Asian woman executive in business attire smiling confidently in modern office environment'
  },
  {
    id: 'fortune-bank',
    company: 'Large ISP/Broadband provider ',
    industry: 'Broadband Service Provider',
    challenge: 'Fragmented customer experience across digital and physical channels',
    solution: 'Deployed PhygitalMax + LeapMax for unified omnichannel experience and process automation',
    results: [
    { metric: 'Improvement in SOP adherence', value: '20%', icon: 'DevicePhoneMobileIcon' },
    { metric: 'AHT reduction', value: '33%', icon: 'BoltIcon' },
    { metric: 'Saved annually on reduced AHT', value: '$185,000', icon: 'BanknotesIcon' }],

    quote: 'The seamless integration between our branches and digital platforms has revolutionized how we serve customers. Our NPS scores have never been higher.',
    author: 'Michael Rodriguez',
    role: 'Head of Digital Banking',
    image: '/assets/images/115_-1771920404136.jpg',
    alt: 'Professional male executive in business suit with confident smile in modern corporate office with city skyline'
  }];


  return (
    <section ref={sectionRef} className={`pb-24 lg:pb-32 bg-white ${className}`}>
      <div className="container mx-auto px-6 lg:px-8">

        {/* Section Header */}
        <div
          className={`text-center max-w-6xl mx-auto mb-16 transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

          <div className="text-left text-xs font-semibold uppercase tracking-widest mb-6">
            <span className="font-body text-sm font-semibold tracking-wide text-[rgba(27,180,215,1)]">Customer Success</span>
          </div>
          <h2 className="sm:text-5xl font-bold mb-8 max-w-3xl text-black text-5xl text-left">
            Proven Transformation Outcomes
          </h2>
          <p className="font-body lg:text-lg text-slate-500 max-w-3xl mb-3 text-lg text-left">
            Real results from enterprise leaders who transformed with UnifyMax
          </p>
        </div>

        {/* Stories */}
        <div className="max-w-6xl mx-auto space-y-10">
          {stories.map((story, index) =>
          <div
            key={story.id}
            className={`bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col lg:flex ${
            index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${index * 150}ms` }}>

              {/* Image Section */}
              <div className="lg:w-2/5 relative h-64 lg:h-auto overflow-hidden">
                <AppImage
                src={story.image}
                alt={story.alt}
                className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent lg:bg-gradient-to-r lg:from-slate-900/60 lg:via-slate-900/20 lg:to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 lg:bottom-8 lg:left-8">
                  <div className="inline-flex items-center space-x-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-3 border border-white/20">
                    <Icon name="BuildingOfficeIcon" size={14} variant="solid" className="text-white" />
                    <span className="font-body text-xs font-semibold text-white">{story.industry}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl lg:text-2xl text-white leading-tight">{story.company}</h3>
                </div>
              </div>

              {/* Content Section */}
              <div className="lg:w-3/5 p-8 lg:p-12 space-y-6">
                {/* Challenge & Solution */}
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="ExclamationTriangleIcon" size={18} variant="solid" className="text-amber-500" />
                      <span className="font-display font-bold text-sm text-amber-500 uppercase tracking-widest">Challenge</span>
                    </div>
                    <p className="font-body text-base text-slate-600 leading-relaxed">{story.challenge}</p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="LightBulbIcon" size={18} variant="solid" className="text-[#1bb4d7]" />
                      <span className="font-display font-bold text-sm text-[#1bb4d7] uppercase tracking-widest">Solution</span>
                    </div>
                    <p className="font-body text-base text-slate-600 leading-relaxed">{story.solution}</p>
                  </div>
                </div>

                {/* Results Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-slate-100">
                  {story.results.map((result, idx) =>
                <div
                  key={idx}
                  className="text-center space-y-2 flex flex-col items-center p-5 bg-slate-50 rounded-xl border border-slate-100 hover:border-[#1bb4d7]/30 hover:bg-[#1bb4d7]/4 transition-all duration-300">

                      <Icon name={result.icon as any} size={28} variant="solid" className="text-[#1bb4d7]" />
                      <div className="font-display font-bold text-2xl text-slate-900 leading-tight">{result.value}</div>
                      <div className="font-body  text-slate-500 leading-snug">{result.metric}</div>
                    </div>
                )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default SuccessStories;